import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'wouter';
import { apiClient } from '@/lib/api-client';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { CheckCircle } from 'lucide-react';

function buildForgotPasswordSchema(t: (key: string, opts?: any) => string) {
  return z.object({
    email: z.string({ message: t('pages.forgotPassword:validation.email.required') })
      .min(1, t('pages.forgotPassword:validation.email.required'))
      .email(t('pages.forgotPassword:validation.email.invalid')),
  });
}

type ForgotPasswordFormData = z.infer<ReturnType<typeof buildForgotPasswordSchema>>;

export default function ForgotPasswordPage() {
  const { t, i18n } = useTranslation(['common', 'pages.forgotPassword']);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();

  const schema = useMemo(() => buildForgotPasswordSchema(t), [t, i18n.language]);
  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    try {
      const response = await apiClient.forgotPassword(data.email, i18n.language);
      
      if (response.success) {
        setIsSuccess(true);
      } else {
        toast({
          title: t('pages.forgotPassword:errors.generic'),
          description: response.message || t('pages.forgotPassword:errors.generic'),
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Forgot password error:', error);
      const errorMessage = error?.message || t('pages.forgotPassword:errors.generic');
      
      // Check if it's a user not found error
      if (errorMessage.toLowerCase().includes('not found') || errorMessage.toLowerCase().includes('user not found')) {
        toast({
          title: t('pages.forgotPassword:errors.emailNotFound'),
          description: errorMessage,
          variant: 'destructive',
        });
      } else {
        toast({
          title: t('pages.forgotPassword:errors.generic'),
          description: errorMessage,
          variant: 'destructive',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <CardTitle className="text-xl font-bold text-dark">{t('pages.forgotPassword:successTitle')}</CardTitle>
            <CardDescription className="mt-2 text-sm text-light">
              {t('pages.forgotPassword:successMessage')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Link href="/login">
              <Button className="w-full" variant="default">
                {t('pages.forgotPassword:backToLogin')}
              </Button>
            </Link>
          </CardContent>
        </Card>
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-dark text-center">{t('pages.forgotPassword:title')}</CardTitle>
          <CardDescription className="text-center text-sm text-light">
            {t('pages.forgotPassword:subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              
              // Manually validate using safeParse to prevent unhandled rejections
              const values = form.getValues();
              const result = schema.safeParse(values);
              
              if (!result.success) {
                // Clear previous errors and set new ones manually
                form.clearErrors();
                for (const issue of result.error.issues) {
                  const field = issue.path?.[0] as keyof ForgotPasswordFormData;
                  if (field) {
                    form.setError(field, {
                      type: 'validation',
                      message: issue.message,
                    }, { shouldFocus: false });
                  }
                }
                return;
              }
              
              // If validation passes, call onSubmit
              onSubmit(result.data);
            }} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-medium text-dark mb-1.5 block">{t('pages.forgotPassword:email')}</FormLabel>
                    <FormControl>
                      <Input 
                        {...field}
                        type="email"
                        placeholder="your@email.com" 
                        disabled={isLoading}
                        autoComplete="email"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <Button 
                type="submit" 
                className="w-full h-10 text-xs font-semibold bg-[var(--primary-blue)] text-white rounded-md shadow-[var(--shadow-subtle)] hover:bg-[#3a7bc8] hover:shadow-[0_4px_12px_rgba(74,144,226,0.2)] transition-all"
                disabled={isLoading}
              >
                {isLoading ? t('pages.forgotPassword:submitting') : t('pages.forgotPassword:submit')}
              </Button>
              
              <div className="text-center">
                <Link href="/login" className="text-primary-blue hover:text-[#3a7bc8] text-xs font-medium">
                  {t('pages.forgotPassword:backToLogin')}
                </Link>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
    </div>
  );
}

